import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	currentTargetRequired,
	getAttributeOrThrow,
	qsaOptional,
	qsOptional,
	qsRequired,
	removeTrapFocus,
	trapFocus,
} from '@/scripts/core/global'
import { UcoastVideo } from '@/scripts/core/ucoast-video'

export class TutorialVideos extends UcoastEl {
	static htmlSelector = 'tutorial-videos'
	primaryNavButtons?: NodeListOf<HTMLButtonElement>
	secondaryNavButtons?: NodeListOf<HTMLButtonElement>
	primaryTabWindows?: NodeListOf<HTMLElement>
	secondaryTabWindows?: NodeListOf<HTMLElement>
	mediaToggleButtons?: NodeListOf<HTMLButtonElement>
	descriptionContents?: NodeListOf<HTMLElement>
	ctaButtons?: NodeListOf<HTMLElement>
	constructor() {
		super()
		this.primaryNavButtons = qsaOptional('[data-primary-nav]', this)
		this.secondaryNavButtons = qsaOptional('[data-secondary-nav]', this)
		this.primaryTabWindows = qsaOptional('[data-primary-tab]', this)
		this.secondaryTabWindows = qsaOptional('[data-secondary-tab]', this)
		this.mediaToggleButtons = qsaOptional('[data-toggle-media]', this)
		this.descriptionContents = qsaOptional('[data-description]', this)
		this.ctaButtons = qsaOptional('.tutorial__cta', this)
		this.primaryNavButtons?.forEach((button) => {
			button.addEventListener(
				'click',
				this.handlePrimaryTabSelect.bind(this)
			)
		})
		this.secondaryNavButtons?.forEach((button) => {
			button.addEventListener(
				'click',
				this.handleSecondaryTabSelect.bind(this)
			)
		})
		this.primaryTabWindows?.forEach((tab) => {
			tab.addEventListener('keyup', (event) => {
				if (event.code?.toUpperCase() === 'ESCAPE') {
					const id = getAttributeOrThrow('data-primary-tab', tab)
					const primaryTabNav = qsOptional(
						`[data-primary-nav="${id}"]`,
						this
					)
					removeTrapFocus(primaryTabNav)
				}
			})
		})
		this.mediaToggleButtons?.forEach((button) => {
			button.addEventListener('click', this.onPauseClick.bind(this))
		})
	}
	handlePrimaryTabSelect(event: Event) {
		const el = currentTargetRequired(event)
		const primaryId = getAttributeOrThrow('data-primary-nav', el)
		this.showPrimaryTab(primaryId)
		const firstSecondaryTab = qsOptional(
			`[data-parent-tab="${primaryId}"][data-secondary-tab]`,
			this
		)
		if (!firstSecondaryTab) {
			return
		}
		const secondaryId = getAttributeOrThrow(
			'data-secondary-tab',
			firstSecondaryTab
		)
		this.showSecondaryTab(secondaryId)
		trapFocus(
			qsRequired(`[data-primary-tab="${primaryId}"]`),
			qsOptional(
				`[data-parent-tab="${primaryId}"][data-secondary-nav]`,
				this
			)
		)
	}
	handleSecondaryTabSelect(event: Event) {
		const el = currentTargetRequired(event)
		const primaryId = getAttributeOrThrow('data-parent-tab', el)
		const secondaryId = getAttributeOrThrow('data-secondary-nav', el)
		this.showPrimaryTab(primaryId)
		this.showSecondaryTab(secondaryId)
	}
	showPrimaryTab(id: string) {
		this.primaryTabWindows?.forEach((tab) => {
			const attr =
				tab.getAttribute('data-primary-tab') === id ? 'false' : 'true'
			tab.setAttribute('aria-hidden', attr)
		})
		this.primaryNavButtons?.forEach((button) => {
			const attr =
				button.getAttribute('data-primary-nav') === id
					? 'true'
					: 'false'
			button.setAttribute('aria-selected', attr)
		})

		this.descriptionContents?.forEach((desc) => {
			const parentId = desc.getAttribute('data-parent-description')
			const hasSecondary = desc.hasAttribute('data-secondary-description')

			if (parentId === id) {
				if (!hasSecondary) {
					desc.setAttribute('aria-hidden', 'false')
					desc.style.display = 'block'
				}
			} else {
				desc.setAttribute('aria-hidden', 'true')
				desc.style.display = 'none'
			}
		})

		this.ctaButtons?.forEach((cta) => {
			const parentId = cta.getAttribute('data-parent-description')
			const hasSecondary = cta.hasAttribute('data-secondary-description')

			if (parentId === id) {
				if (!hasSecondary) {
					cta.setAttribute('aria-hidden', 'false')
					cta.style.display = 'flex'
				}
			} else {
				cta.setAttribute('aria-hidden', 'true')
				cta.style.display = 'none'
			}
		})

		const primaryTabNav = qsOptional(`[data-primary-nav="${id}"]`, this)
		if (!primaryTabNav) {
			return
		}
	}
	showSecondaryTab(id: string) {
		this.secondaryTabWindows?.forEach((tab) => {
			const attr =
				tab.getAttribute('data-secondary-tab') === id ? 'false' : 'true'
			tab.setAttribute('aria-hidden', attr)
		})
		this.secondaryNavButtons?.forEach((button) => {
			const attr =
				button.getAttribute('data-secondary-nav') === id
					? 'true'
					: 'false'
			button.setAttribute('aria-expanded', attr)
		})

		this.descriptionContents?.forEach((desc) => {
			const secondaryId = desc.getAttribute('data-secondary-description')
			if (secondaryId) {
				const isVisible = secondaryId === id
				desc.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
				desc.style.display = isVisible ? 'block' : 'none'
			}
		})

		this.ctaButtons?.forEach((cta) => {
			const secondaryId = cta.getAttribute('data-secondary-description')
			if (secondaryId) {
				const isVisible = secondaryId === id
				cta.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
				cta.style.display = isVisible ? 'flex' : 'none'
			}
		})

		this.pauseAllTutorialVideos()
		const primaryTabNav = qsOptional(`[data-primary-nav="${id}"]`, this)
		if (!primaryTabNav) {
			return
		}
	}
	async pauseAllTutorialVideos() {
		const tutorialVideos = qsaOptional<UcoastVideo>('tutorial-videos ucoast-video')
		if (!tutorialVideos) return
		for (let video of tutorialVideos) {
			if (video.isPlaying) {
				await video.pauseInPlace()
			}
		}
	}
	/*playVideoInTab(id: string) {
		const videos = qsaOptional<UcoastVideo>('ucoast-video', this)
		if (!videos) return
		videos.forEach((video) => {
			const mediaId = getAttributeOrThrow('data-media-id', video)
			if (mediaId === id) {
				//video.videoEl.muted = false
				void video.playInPlace()
			} else {
				//video.videoEl.muted = false
				void video.pause()
			}
		})
		this.mediaToggleButtons?.forEach((button) => {
			const mediaId = getAttributeOrThrow('data-toggle-media', button)
			if (mediaId === id) {
				button.setAttribute('data-paused', 'false')
			} else {
				button.setAttribute('data-paused', 'true')
			}
		})
		const video = qsRequired<UcoastVideo>(
			`ucoast-video[data-media-id="${id}"]`
		)
		void video.playInPlace()
	}*/
	onPauseClick(event: Event) {
		const target = currentTargetRequired(event)
		const mediaId = getAttributeOrThrow(
			'data-toggle-media',
			target
		)
		const video = qsRequired<UcoastVideo>(
			`ucoast-video[data-media-id="${mediaId}"]`
		)
		if (video.isPlaying) {
			target.setAttribute('data-paused', 'true')
			void video.pauseInPlace()
		} else {
			this.pauseAllTutorialVideos().then(() => {
				target.setAttribute('data-paused', 'false')
				void video.playInPlace()
			}).catch(e => {
				console.warn('Error pausing all tutorial videos', e)
				target.setAttribute('data-paused', 'false')
				void video.playInPlace()
			})
		}
	}
}
